import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { teamKey, teamQuery } from "@/lib/screen-queries";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Copy, Trash2, Clock, CheckCircle2, AlertTriangle, Send, UserMinus, Crown } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { friendlyAuthError } from "@/lib/auth-errors";
import { SiteRbacPanel } from "@/components/team/SiteRbacPanel";
import { ROLE_CATALOG } from "@/lib/site-rbac";
import { createInvite, revokeInvite, resendInvite, removeMember, updateMemberRole } from "@/lib/invites.functions";
import { usePlanLimits, planDisplayName } from "@/lib/plan-limits";
import { UpgradePrompt } from "@/components/UpgradePrompt";

export const Route = createFileRoute("/_authenticated/team")({
  component: TeamPage,
  head: () => ({ meta: [{ title: "Team — MissionReady" }] }),
});

type Member = { id: string; user_id: string; role: string; email: string | null; full_name: string | null };
type Invite = {
  id: string;
  email: string;
  role: string;
  token: string;
  accepted_at: string | null;
  expires_at: string | null;
  created_at: string;
};

function inviteStatus(inv: Invite): "accepted" | "expired" | "pending" {
  if (inv.accepted_at) return "accepted";
  if (inv.expires_at && new Date(inv.expires_at) < new Date()) return "expired";
  return "pending";
}

function TeamPage() {
  const { currentOrg, currentRole, user } = useAuth();
  const canManage = ["org_owner", "admin"].includes(currentRole ?? "");
  // Members + invites live in the shared cache entry the prefetcher warms.
  const qc = useQueryClient();
  const orgId = currentOrg?.id ?? "";
  const userId = user?.id ?? "";
  const { data: teamData } = useQuery(teamQuery(orgId, userId));
  const members = (teamData?.members ?? []) as Member[];
  const invites = (teamData?.invites ?? []) as Invite[];
  const [open, setOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("officer");
  const [busy, setBusy] = useState(false);
  const createInviteFn = useServerFn(createInvite);
  const revokeInviteFn = useServerFn(revokeInvite);
  const resendInviteFn = useServerFn(resendInvite);
  const removeMemberFn = useServerFn(removeMember);
  const updateMemberRoleFn = useServerFn(updateMemberRole);
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);
  const limits = usePlanLimits(currentOrg?.id ?? null);

  const pendingCount = invites.filter((i) => inviteStatus(i) === "pending").length;
  const seatsInUse = members.length + pendingCount;
  const atSeatCap = limits.maxSeats != null && seatsInUse >= limits.maxSeats;

  // Refresh after invite/member changes by refetching the shared cache entry.
  const load = async () => {
    if (!orgId) return;
    await qc.invalidateQueries({ queryKey: teamKey(orgId, userId) });
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !user) return;
    if (atSeatCap) { setOpen(false); setUpgradeOpen(true); return; }
    setBusy(true);
    let token: string | undefined;
    let emailSent = false;
    let emailError: string | undefined;
    try {
      const res: any = await createInviteFn({
        data: {
          orgId: currentOrg.id,
          email: email.trim().toLowerCase(),
          role: role as any,
          origin: typeof window !== "undefined" ? window.location.origin : undefined,
        },
      });
      token = res.token;
      emailSent = Boolean(res?.emailSent);
      emailError = res?.emailError;
    } catch (err: any) {
      setBusy(false);
      return toast.error(friendlyAuthError(err));
    }
    setBusy(false);
    if (token && typeof window !== "undefined") {
      const link = `${window.location.origin}/accept-invite/${token}`;
      try { await navigator.clipboard.writeText(link); } catch { /* clipboard optional */ }
      if (emailSent) toast.success(`Invite emailed to ${email.trim().toLowerCase()} — link also copied`);
      else toast.warning(`Invite created, but the email couldn't be sent${emailError ? ` (${emailError})` : ""}. Link copied — share it directly.`);
    } else {
      toast.success("Invite created");
    }
    setEmail(""); setOpen(false);
    await load();
    await limits.refresh();
  };

  const revoke = async (id: string) => {
    if (typeof window !== "undefined" && !window.confirm("Revoke this invitation?")) return;
    try {
      await revokeInviteFn({ data: { inviteId: id } });
    } catch (err: any) { return toast.error(friendlyAuthError(err)); }
    toast.success("Invite revoked — seat freed");
    await load();
    await limits.refresh();
  };

  const resend = async (id: string, fallbackToken: string) => {
    try {
      const updated: any = await resendInviteFn({
        data: { inviteId: id, origin: typeof window !== "undefined" ? window.location.origin : undefined },
      });
      const token = updated?.token ?? fallbackToken;
      if (token && typeof window !== "undefined") {
        const link = `${window.location.origin}/accept-invite/${token}`;
        try { await navigator.clipboard.writeText(link); } catch { /* clipboard optional */ }
      }
      if (updated?.emailSent) toast.success(`Invite re-sent to ${updated.email} — link also copied`);
      else toast.warning(`Invite extended, but the email couldn't be sent${updated?.emailError ? ` (${updated.emailError})` : ""}. Link copied — share it directly.`);
      await load();
    } catch (err: any) { toast.error(friendlyAuthError(err)); }
  };


  const changeRole = async (m: Member, nextRole: string) => {
    if (!currentOrg || nextRole === m.role) return;
    setSavingRoleId(m.id);
    try {
      await updateMemberRoleFn({ data: { orgId: currentOrg.id, memberId: m.id, role: nextRole as any } });
      const label = ROLE_CATALOG.find((r) => r.value === nextRole)?.label ?? nextRole;
      toast.success(`${m.full_name || m.email || "Member"} is now ${label}`);
      await load();
    } catch (err: any) {
      toast.error(friendlyAuthError(err));
    } finally {
      setSavingRoleId(null);
    }
  };

  const remove = async (m: Member) => {
    if (!currentOrg) return;
    if (m.role === "org_owner") return toast.error("The account owner can't be removed.");
    if (user && m.user_id === user.id) return toast.error("You can't remove yourself.");
    const label = m.full_name || m.email || "this member";
    if (typeof window !== "undefined" && !window.confirm(`Remove ${label}? This frees their seat immediately.`)) return;
    try {
      await removeMemberFn({ data: { orgId: currentOrg.id, memberId: m.id } });
    } catch (err: any) { return toast.error(friendlyAuthError(err)); }
    toast.success("Member removed — seat freed");
    await load();
    await limits.refresh();
  };

  const copyLink = async (token: string) => {
    if (typeof window === "undefined") return;
    const link = `${window.location.origin}/accept-invite/${token}`;
    try { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); }
    catch { toast.error("Couldn't copy. Long-press to copy: " + link); }
  };

  const pending = invites.filter((i) => inviteStatus(i) === "pending");
  const expired = invites.filter((i) => inviteStatus(i) === "expired");
  const accepted = invites.filter((i) => inviteStatus(i) === "accepted");

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold md:text-3xl">Team</h1>
          <p className="truncate text-sm text-muted-foreground">
            Members and invitations for {currentOrg?.name}.
            {limits.maxSeats != null && (
              <> · <span className="font-medium">{seatsInUse}/{limits.maxSeats}</span> seats used on {planDisplayName(limits.planKey)}</>
            )}
          </p>
        </div>
        {canManage && (
          <>
            {atSeatCap ? (
              <Button size="sm" onClick={() => setUpgradeOpen(true)}>
                <Plus className="mr-1 h-4 w-4" /> Invite
              </Button>
            ) : (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button size="sm"><Plus className="mr-1 h-4 w-4" /> Invite</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Invite team member</DialogTitle></DialogHeader>
                  <form onSubmit={sendInvite} className="space-y-4">
                    <div>
                      <Label htmlFor="e">Email</Label>
                      <Input id="e" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" autoComplete="email" />
                    </div>
                    <div>
                      <Label htmlFor="r">Role</Label>
                      <select id="r" value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 block w-full rounded-md border border-input bg-input px-3 py-2 text-sm">
                        {ROLE_CATALOG.filter((r) => r.value !== "org_owner").map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-muted-foreground">Invitations expire after 7 days.</p>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={busy}>{busy ? "Sending…" : "Send invite"}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </>
        )}
      </div>

      {canManage && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
          <div className="min-w-0">
            <h2 className="font-display text-base font-semibold">Contractor Partners</h2>
            <p className="text-sm text-muted-foreground">
              Third-party contractor organizations working your sites. They don't use employee seats.
            </p>
          </div>
          <Button size="sm" variant="outline" asChild>
            <Link to="/contractors">Open Contractor Partners</Link>
          </Button>
        </div>
      )}

      {atSeatCap && (
        <div className="mt-4 rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm">
          <div className="font-semibold">You've used all your seats</div>
          <p className="mt-1 text-muted-foreground">
            {planDisplayName(limits.planKey)} includes {limits.maxSeats} seat{limits.maxSeats === 1 ? "" : "s"}.
            Upgrade to invite more officers.
          </p>
        </div>
      )}

      <UpgradePrompt
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        planName={planDisplayName(limits.planKey)}
        reason={`You can have up to ${limits.maxSeats} seat${limits.maxSeats === 1 ? "" : "s"} on this plan and you've already filled them. Pick a plan to invite more officers.`}
        returnTo="/team"
      />


      <Tabs defaultValue="members" className="mt-6">
        <TabsList>
          <TabsTrigger value="members">Members & invites</TabsTrigger>
          <TabsTrigger value="access">Site access</TabsTrigger>
        </TabsList>
        <TabsContent value="members" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Section title={`Members (${members.length})`}>
              {members.length === 0 && <Empty text="No members yet." />}
              {members.map((m) => {
                const isYou = user?.id === m.user_id;
                const isOwner = m.role === "org_owner";
                const display = m.full_name || m.email || (m.user_id.slice(0, 8) + "…");
                return (
                  <div key={m.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {isOwner && <Crown className="h-3.5 w-3.5 text-primary" aria-label="Account owner" />}
                        <span className="truncate font-medium">{display}</span>
                        {isYou && <span className="rounded-full border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-primary">You</span>}
                      </div>
                      {m.email && m.full_name && (
                        <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {canManage && !isOwner && !isYou ? (
                        <select
                          aria-label={`Role for ${display}`}
                          value={m.role}
                          disabled={savingRoleId === m.id || (currentRole !== "org_owner" && m.role === "admin")}
                          onChange={(e) => changeRole(m, e.target.value)}
                          className="rounded-md border border-input bg-input px-2 py-1 text-xs disabled:opacity-60"
                        >
                          {ROLE_CATALOG.filter((r) => r.value !== "org_owner")
                            .filter(
                              (r) =>
                                currentRole === "org_owner" ||
                                r.value !== "admin" ||
                                m.role === "admin",
                            )
                            .map((r) => (
                              <option
                                key={r.value}
                                value={r.value}
                                disabled={currentRole !== "org_owner" && r.value === "admin"}
                              >
                                {r.label}
                              </option>
                            ))}
                        </select>
                      ) : (
                        <Badge>{ROLE_CATALOG.find((r) => r.value === m.role)?.label ?? m.role}</Badge>
                      )}
                      {canManage && !isOwner && !isYou && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive"
                                onClick={() => remove(m)} title="Remove from organization">
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </Section>

            <Section title={`Pending invites (${pending.length})`}>
              {pending.length === 0 && <Empty text="No pending invites." />}
              {pending.map((i) => (
                <InviteRow key={i.id} invite={i} canManage={canManage} onCopy={copyLink} onRevoke={revoke} onResend={resend} />
              ))}
              {expired.length > 0 && (
                <div className="mt-4 border-t border-border pt-3">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Expired</h3>
                  {expired.map((i) => (
                    <InviteRow key={i.id} invite={i} canManage={canManage} onCopy={copyLink} onRevoke={revoke} onResend={resend} />
                  ))}
                </div>
              )}
              {accepted.length > 0 && (
                <div className="mt-4 border-t border-border pt-3">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Accepted ({accepted.length})</h3>
                  {accepted.slice(0, 5).map((i) => (
                    <InviteRow key={i.id} invite={i} canManage={canManage} onCopy={copyLink} onRevoke={revoke} onResend={resend} />
                  ))}
                </div>
              )}
            </Section>
          </div>
        </TabsContent>
        <TabsContent value="access" className="mt-4">
          <SiteRbacPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InviteRow({
  invite, canManage, onCopy, onRevoke, onResend,
}: {
  invite: Invite;
  canManage: boolean;
  onCopy: (token: string) => void;
  onRevoke: (id: string) => void;
  onResend: (id: string, fallbackToken: string) => void;
}) {
  const status = inviteStatus(invite);
  const expires = invite.expires_at ? new Date(invite.expires_at) : null;
  const daysLeft = expires ? Math.ceil((expires.getTime() - Date.now()) / 86_400_000) : null;
  return (
    <div className="flex items-center justify-between gap-2 py-2 text-sm">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{invite.email}</div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <Badge>{invite.role}</Badge>
          {status === "accepted" && (
            <span className="inline-flex items-center gap-1 text-success"><CheckCircle2 className="h-3 w-3" /> Accepted</span>
          )}
          {status === "expired" && (
            <span className="inline-flex items-center gap-1 text-destructive"><AlertTriangle className="h-3 w-3" /> Expired</span>
          )}
          {status === "pending" && expires && (
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {daysLeft! > 0 ? `${daysLeft}d left` : "expires today"}</span>
          )}
          {expires && (
            <span className="text-[11px]">· exp {expires.toLocaleDateString()}</span>
          )}
        </div>
      </div>
      {canManage && status !== "accepted" && (
        <div className="flex items-center gap-1">
          {status === "pending" && (
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onCopy(invite.token)} title="Copy invite link">
              <Copy className="h-4 w-4" />
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-8 gap-1 px-2" onClick={() => onResend(invite.id, invite.token)} title={status === "expired" ? "Renew invite" : "Resend invite email"}>
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">{status === "expired" ? "Renew" : "Resend"}</span>
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => onRevoke(invite.id)} title="Revoke invite">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      <div className="mt-3 divide-y divide-border">{children}</div>
    </div>
  );
}
function Row({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  return <div className="flex items-center justify-between py-2 text-sm"><span>{left}</span>{right}</div>;
}
function Badge({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-xs">{children}</span>;
}
function Empty({ text }: { text: string }) {
  return <div className="py-6 text-center text-sm text-muted-foreground">{text}</div>;
}
